/**
 * ═══════════════════════════════════════════════════════════════════════════
 * KINDTABELLEN NUR SENDEN, WENN SIE SICH GEAENDERT HABEN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ─── Anlass ─────────────────────────────────────────────────────────────────
 * `saveProjectToCloud` schickt bei JEDEM Speichern alle Zeilen aller acht
 * Kindtabellen an `save_project_children_tx` — beim Vielnutzer rund 3000 Mal
 * im Monat, je Speichern ~50 KB unkomprimiert (Mängel ~40 KB, der Rest ~10 KB).
 * Meist hat sich dabei genau eine Tabelle geändert, oft gar keine.
 *
 * ─── Was die RPC dafür hergibt (Nr. 135, `save_project_children_tx_schnell.sql`)
 * „fehlt eine [Tabelle] im Payload, wird sie nicht angefasst" — kein Upsert,
 * kein Prune. Eine Tabelle wegzulassen ist damit dasselbe, als hätte man sie
 * unverändert noch einmal geschickt — VORAUSGESETZT, der Server hält für diese
 * Tabelle noch genau das, was dieser Tab zuletzt bestätigt geschrieben hat.
 * Ein erneutes Senden desselben Arrays änderte dann nichts (gleiche Zeilen →
 * kein Update, gleiche Behalte-Liste → nichts zu prunen). Weggelassen wird
 * also nie etwas, das der Server nicht schon hat.
 *
 * ─── Die Regel ──────────────────────────────────────────────────────────────
 * Weggelassen wird eine Tabelle NUR, wenn ALLES zutrifft:
 *   1. Der Aufrufer hat ausdrücklich `uebertragung: 'sparsam'` verlangt
 *      (heute nur der Normalfall der Warteschlange). Jeder andere Aufrufer —
 *      Wiederherstellung, Entwurf verwerfen, Umbuchung, Rechnung, Bergung —
 *      schickt wie bisher alles.
 *   2. Es gibt einen gemerkten Stand dieses Tabs für dieses Projekt, er stammt
 *      vom selben Benutzer, und er ist selbst aus einem Speichern entstanden,
 *      das auf einem EIGENEN gemerkten Stand aufsetzte (`inFolge`). Das erste
 *      Speichern nach einem Laden schickt deshalb immer alles, das zweite auch
 *      noch — erst ab dem dritten wird weggelassen (siehe „Restrisiko").
 *   3. Der Anker dieses Speicherns ist GENAU die Version, die nach dem
 *      gemerkten Schreiben auf dem Server stand, und das bedingte Update hat
 *      im ersten Versuch getroffen. Zwischen dem gemerkten Schreiben und jetzt
 *      hat also niemand `projects` gespeichert — und jedes Kindtabellen-
 *      Schreiben über `saveProjectToCloud` schreibt vorher `projects` mit
 *      neuem `updated_at`.
 *   4. Keine Bergung, keine Zähler vom Server (`zaehlerVomServer`).
 *   5. In diesem Tab lief kein zweites Speichern desselben Projekts parallel —
 *      weder beim Merken noch jetzt (zwei Läufe können auf dem Server in
 *      anderer Reihenfolge ankommen, als sie hier fertig werden).
 *   6. Die Serialisierung (`JSON.stringify`) des Arrays, das jetzt gesendet
 *      würde, ist ZEICHENGLEICH mit der gemerkten. Kein Hash — eine Kollision
 *      wäre ein stiller Datenverlust.
 *
 * ─── Wann der gemerkte Stand verfällt ───────────────────────────────────────
 * Der Stand wird zu Beginn JEDES Speicherns entnommen (nicht nur gelesen) und
 * erst nach dessen bestätigtem Ende neu gesetzt. Jeder Fehler, jeder Abbruch,
 * jede Unsicherheit (RPC-Fehler, Netz, Build-Riegel, Konflikt) lässt damit
 * KEINEN Stand zurück — das nächste Speichern schickt alles. Neu gesetzt wird
 * nur nach einem „sauberen" Speichern: Anker gesetzt und im ersten Versuch
 * getroffen, keine Bergung, keine Zähler vom Server, kein paralleler Lauf.
 * Tabellen, die das Speichern gar nicht trug (`items`/`checklists` fehlen),
 * fallen aus dem Stand heraus.
 *
 * ─── Restrisiko, ausdrücklich ───────────────────────────────────────────────
 * Ein Schreiber, der Kindtabellen anfasst, OHNE `projects.updated_at` zu
 * ändern (direkte Updates/Deletes, Server-RPCs, die RPC eines anderen Geräts,
 * die erst NACH unserem Schreiben ankommt), bleibt für diese Prüfung
 * unsichtbar. Dann wird eine unveränderte Tabelle nicht erneut über seine
 * Änderung geschrieben — der Server behält seine. Bisher hätte das
 * Vollschreiben sie mit dem unveränderten lokalen Stand überschrieben (und
 * eine fremde Neuanlage weggeprunt). Regel 2 schiebt das Fenster für die
 * verspätete fremde RPC auf zwei eigene Speichervorgänge hinaus.
 *
 * Rein im Speicher dieses Tabs, nach einem Neuladen leer. Ohne React, ohne
 * Supabase — vollständig prüfbar (`childSaveDelta.test.ts`).
 */

/** Was dieser Tab zuletzt bestätigt in die Kindtabellen eines Projekts geschrieben hat. */
export interface KindStand {
    /** `projects.updated_at`, wie der Server es nach dem merkenden Speichern meldete. */
    version: string;
    /** Wer geschrieben hat. Ein anderer Benutzer im selben Tab erbt nichts. */
    nutzerId: string;
    /** Entstand dieser Stand aus einem Speichern, das selbst auf einem eigenen gemerkten Stand aufsetzte? */
    inFolge: boolean;
    /** Je Tabelle: `JSON.stringify` genau des Arrays, das der Server danach hält. */
    tabellen: ReadonlyMap<string, string>;
}

/** Wie viel ein Speichern übertragen darf. Fehlt der Wert, gilt 'voll'. */
export type Uebertragung = 'voll' | 'sparsam';

/** Ein Speichervorgang aus Sicht dieses Moduls: geöffnet, entschieden, geschlossen. */
export interface KindLauf {
    projektId: string | null;
    /** Der zu Beginn ENTNOMMENE Stand (danach steht für dieses Projekt keiner mehr da). */
    vorher: KindStand | null;
    /** Lief beim Öffnen schon ein anderes Speichern desselben Projekts in diesem Tab? */
    ueberlappt: boolean;
    epoche: number;
    /** Der Stand, den dieser Lauf nach seinem bestätigten Ende setzen darf. */
    neu: KindStand | null;
}

const MAX_PROJEKTE = 8;

const staende = new Map<string, KindStand>();
const laeufe = new Map<string, { aktiv: number; epoche: number }>();

const brauchbar = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;

export const neuerKindLauf = (): KindLauf => ({
    projektId: null, vorher: null, ueberlappt: false, epoche: 0, neu: null,
});

/**
 * Beginnt einen Lauf für ein BESTEHENDES Projekt: entnimmt den gemerkten
 * Stand und meldet den Lauf an. Ein zweites Öffnen desselben Laufs ist
 * wirkungslos.
 */
export const oeffneKindLauf = (lauf: KindLauf, projektId: string | null | undefined): void => {
    if (lauf.projektId || !brauchbar(projektId)) return;
    const zaehler = laeufe.get(projektId) ?? { aktiv: 0, epoche: 0 };
    lauf.projektId = projektId;
    lauf.ueberlappt = zaehler.aktiv > 0;
    zaehler.aktiv += 1;
    zaehler.epoche += 1;
    lauf.epoche = zaehler.epoche;
    laeufe.set(projektId, zaehler);
    lauf.vorher = staende.get(projektId) ?? null;
    staende.delete(projektId);
};

/**
 * Beendet den Lauf. Gesetzt wird `lauf.neu` nur, wenn das Speichern
 * vollständig gelungen ist UND kein anderer Lauf desselben Projekts sich mit
 * diesem überschnitten hat (weder beim Öffnen noch seither). Sonst bleibt kein
 * Stand stehen — das nächste Speichern schickt alles.
 */
export const schliesseKindLauf = (lauf: KindLauf, erfolgreich: boolean): void => {
    const projektId = lauf.projektId;
    if (!projektId) return;
    lauf.projektId = null;
    const zaehler = laeufe.get(projektId);
    const ungestoert = !!zaehler && !lauf.ueberlappt && zaehler.epoche === lauf.epoche;
    if (zaehler) {
        zaehler.aktiv -= 1;
        if (zaehler.aktiv <= 0) laeufe.delete(projektId);
    }
    if (!erfolgreich || !ungestoert || !lauf.neu) return;
    staende.delete(projektId);
    staende.set(projektId, lauf.neu);
    // Begrenzt: die ältesten Projekte fallen heraus (sie schicken dann einmal alles).
    while (staende.size > MAX_PROJEKTE) {
        const aeltester = staende.keys().next().value;
        if (aeltester === undefined) break;
        staende.delete(aeltester);
    }
};

/** Was die Entscheidung über das Weglassen kennen muss. */
export interface WeglassLage {
    uebertragung: Uebertragung | undefined;
    vorher: KindStand | null;
    ueberlappt: boolean;
    /** Der Anker, gegen den das bedingte Update lief — `null` = ohne Bedingung. */
    anker: string | null;
    /** Hat das bedingte Update im ERSTEN Versuch eine Zeile getroffen? */
    ankerGetroffen: boolean;
    zaehlerVomServer: boolean;
    nutzerId: string | null | undefined;
}

/** Regel 1–5 (siehe Kopf). Regel 6 prüft `waehleKindTabellen` je Tabelle. */
export const darfKindTabellenWeglassen = (lage: WeglassLage): boolean => {
    const { vorher } = lage;
    return lage.uebertragung === 'sparsam'
        && !!vorher
        && vorher.inFolge
        && !lage.ueberlappt
        && brauchbar(lage.nutzerId) && vorher.nutzerId === lage.nutzerId
        && brauchbar(lage.anker) && vorher.version === lage.anker
        && lage.ankerGetroffen
        && !lage.zaehlerVomServer;
};

export interface KindTabelle {
    table: string;
    rows: unknown[];
}

export interface KindAuswahl<T extends KindTabelle> {
    senden: T[];
    weggelassen: string[];
    /** Je Tabelle die Serialisierung, gegen die verglichen wurde (für den nächsten Stand). */
    serialisiert: Map<string, string>;
}

/**
 * Teilt die Kindtabellen in „senden" und „weglassen". Ohne Erlaubnis oder
 * ohne Stand wird alles gesendet. `immerSenden` erzwingt einzelne Tabellen
 * (z. B. `project_services`, wenn gerettete Zeilen mitgehen müssen).
 */
export const waehleKindTabellen = <T extends KindTabelle>(
    tabellen: readonly T[],
    vorher: KindStand | null,
    darfWeglassen: boolean,
    immerSenden: ReadonlySet<string> = new Set(),
): KindAuswahl<T> => {
    const senden: T[] = [];
    const weggelassen: string[] = [];
    const serialisiert = new Map<string, string>();
    for (const t of tabellen) {
        const json = JSON.stringify(t.rows);
        serialisiert.set(t.table, json);
        const gleich = darfWeglassen && !!vorher && !immerSenden.has(t.table)
            && vorher.tabellen.get(t.table) === json;
        if (gleich) weggelassen.push(t.table);
        else senden.push(t);
    }
    return { senden, weggelassen, serialisiert };
};

export interface NaechsterStandLage {
    vorher: KindStand | null;
    anker: string | null;
    ankerGetroffen: boolean;
    zaehlerVomServer: boolean;
    nutzerId: string | null | undefined;
    /** `updated_at` aus der Antwort des bedingten Updates. */
    version: unknown;
    serialisiert: ReadonlyMap<string, string>;
    /** Tabellen, die nicht gemerkt werden (Sonderfälle — sie gehen nächstes Mal sicher mit). */
    nichtMerken?: ReadonlySet<string>;
}

/**
 * Der Stand nach einem bestätigten Kindtabellen-Schreiben — oder `null`, wenn
 * das Speichern nicht „sauber" war (dann merkt sich der Tab nichts).
 */
export const naechsterKindStand = (lage: NaechsterStandLage): KindStand | null => {
    if (!brauchbar(lage.anker) || !lage.ankerGetroffen || lage.zaehlerVomServer) return null;
    if (!brauchbar(lage.nutzerId) || !brauchbar(lage.version)) return null;
    const tabellen = new Map<string, string>();
    for (const [tabelle, json] of lage.serialisiert) {
        if (!lage.nichtMerken?.has(tabelle)) tabellen.set(tabelle, json);
    }
    const vorher = lage.vorher;
    return {
        version: lage.version,
        nutzerId: lage.nutzerId,
        inFolge: !!vorher && vorher.nutzerId === lage.nutzerId && vorher.version === lage.anker,
        tabellen,
    };
};

/** Vergisst den Stand eines Projekts (oder aller) — das nächste Speichern schickt alles. */
export const vergissKindStand = (projektId?: string | null): void => {
    if (projektId === undefined) staende.clear();
    else if (brauchbar(projektId)) staende.delete(projektId);
};

/** Nur für Tests: Stände und Läufe leeren. */
export const setzeKindStaendeZurueck = (): void => {
    staende.clear();
    laeufe.clear();
};

/** Nur für Tests: der gemerkte Stand eines Projekts (ohne ihn zu entnehmen). */
export const gemerkterKindStand = (projektId: string): KindStand | null => staende.get(projektId) ?? null;
